import discoAds from 'assets/adsImagery/imagery-ads.png';
import discoAds2x from 'assets/adsImagery/imagery-ads@2x.png';
import discoAds3x from 'assets/adsImagery/imagery-ads@3x.png';
import ImageCardWithCenteredContents from 'components/ImageCardWithCenteredContents';
import { useNotification } from 'components/Notification';
import { DashboardContent } from 'pages/Dashboard';
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useEventTracking } from 'state/eventTracking';
import { useIsProGrow } from 'state/pricing';
import CampaignCreationProvider, {
  useCampaignCreationState,
} from './CampaignCreationContext';
import CampaignCreationView from './CampaignCreationView';
import ConfirmationView from './ConfirmationView';
import IntroView from './IntroView';
import ReviewView from './ReviewView';
import { useSelfServeSponsorRequestDraft } from './state';
import { adCampaignsQueries } from './state/queryKeys';
import { checkHasContentDetail } from './state/utils';
import { AdCampaignContentView } from './types';
import useCheckCreateAdStatus from './useCheckCreateAdStatus';
import useTrackAdCampaignPage from './useTrackAdCampaignPage';

export interface ADCampaignPageProps {}

const ADCampaignPage: React.FC<ADCampaignPageProps> = () => {
  const [view, setView] = useState<AdCampaignContentView>('intro');
  const [prefillRequestId, setPrefillRequestId] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const [state, dispatch] = useCampaignCreationState();
  const { showSuccess } = useNotification();
  const { trackCreatePromoCheckout } = useEventTracking();

  const { data: prefillCampaign } =
    useSelfServeSponsorRequestDraft(prefillRequestId);
  const { data: isProGrow, isLoading: isProGrowLoading } = useIsProGrow();

  const handleSuccess = useCallback(() => {
    setView('confirmation');

    queryClient.invalidateQueries(adCampaignsQueries.adCampaignInfo());

    dispatch({
      type: 'RESET_VALUES',
    });
    showSuccess(
      'Thanks for setting up your campaign with Headliner! An account rep will reach out soon to review your content with you and answer any questions before making your campaign live.',
    );
    trackCreatePromoCheckout(true);
  }, [dispatch, queryClient, showSuccess, trackCreatePromoCheckout]);

  const handleCheckoutCancel = useCallback(
    (selfServeSponsorRequestId: number | null): void => {
      if (selfServeSponsorRequestId) {
        setPrefillRequestId(selfServeSponsorRequestId);
      }
    },
    [],
  );

  useCheckCreateAdStatus({
    onSuccess: handleSuccess,
    onCancel: handleCheckoutCancel,
  });
  useTrackAdCampaignPage();

  const srcSet = `${discoAds} 1x, ${discoAds2x} 2x, ${discoAds3x} 3x`;

  const createHandleGoToView = useCallback(
    (nextView: AdCampaignContentView) => () => {
      setView(nextView);
    },
    [],
  );

  const handleCancel = useCallback((): void => {
    setView('intro');
    dispatch({
      type: 'RESET_VALUES',
    });
    dispatch({
      type: 'GO_TO_STEP',
      payload: {
        step: 'episode',
      },
    });
  }, [dispatch]);

  useEffect(() => {
    if (!prefillCampaign || isProGrowLoading) {
      return;
    }

    if (checkHasContentDetail(prefillCampaign)) {
      dispatch({
        type: 'RE_RUN_CAMPAIGN',
        payload: { campaign: prefillCampaign, isProGrow },
      });
      setView('review');
    }

    setPrefillRequestId(null);
  }, [prefillCampaign, isProGrow, isProGrowLoading, dispatch]);

  return (
    <DashboardContent maxWidth="md">
      <ImageCardWithCenteredContents src={discoAds} srcSet={srcSet}>
        <CampaignCreationProvider {...{ state, dispatch }}>
          {view === 'intro' && (
            <IntroView
              onCreateNewCampaign={createHandleGoToView('campaign-creation')}
              onRunAgain={createHandleGoToView('review')}
            />
          )}

          {view === 'campaign-creation' && (
            <CampaignCreationView
              onCancel={handleCancel}
              onFinishCreation={createHandleGoToView('review')}
            />
          )}

          {view === 'review' && (
            <ReviewView
              onGoBack={createHandleGoToView('campaign-creation')}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
            />
          )}

          {view === 'confirmation' && (
            <ConfirmationView
              onCreateAnotherCampaign={createHandleGoToView(
                'campaign-creation',
              )}
              onCancel={handleCancel}
            />
          )}
        </CampaignCreationProvider>
      </ImageCardWithCenteredContents>
    </DashboardContent>
  );
};

export default ADCampaignPage;
